import * as z from 'zod';
import { legacyHttp, unwrapData } from '@noor/api';
import { legacyOrderListSchema, type LegacyOrderList } from '../model/schema';
import { legacyOrderDetailSchema, type LegacyOrderDetail } from '../model/detail-schema';
import { LEGACY_STATUS_TABS, LEGACY_ORDERS_PAGE_SIZE } from '../config/order-stages';

/**
 * The list query as the old panel built it. The endpoint takes a flat set of
 * boolean flags rather than one `status` param, and — importantly — it expects
 * ABSENT flags for "don't filter", not `false`: sending `is_active=false`
 * filters for inactive orders instead of disabling the filter. That is why the
 * status → params mapping below emits a single key or nothing at all, and why
 * `is_paid` / `is_courier_debt` carry an explicit `false` (there the `false`
 * *is* the filter).
 */
export interface LegacyOrdersQuery {
  /** Active status pill id — see `LEGACY_STATUS_TABS`. */
  status: string;
  /** Free-text search over the order id. */
  search?: string | undefined;
  /** Inclusive start date, `YYYY-MM-DD`. */
  fromDate: string;
  /** EXCLUSIVE end date, `YYYY-MM-DD` — the old page always sent `end + 1 day`. */
  tillDate: string;
  /** Zero-based row offset. */
  skip: number;
  limit?: number;
}

/** Maps a status pill onto the endpoint's boolean flag (or nothing for "all"). */
function statusParams(status: string): Record<string, boolean> {
  const tab = LEGACY_STATUS_TABS.find((t) => t.id === status);
  if (!tab?.filter) return {};
  return { [tab.filter.param]: tab.filter.value };
}

/**
 * The courier's live position for one order, `GET
 * orders/{order}/couriers/{courier}/location`. `location` is where the courier
 * is; `target_destination` is where the DISPATCHER is sending them next, which
 * can differ from the order's own next stop (e.g. a chained pickup).
 */
/**
 * A tracking fix. Tolerant where the shared `legacyPointSchema` is strict:
 * this backend has shipped numbers as strings before (`floor`), so numeric
 * strings coerce; anything non-numeric still fails the parse (falling back to
 * the order's own fix) rather than pinning a NaN coordinate.
 */
const legacyTrackingPointSchema = z.object({
  lat: z.coerce
    .number()
    .refine((n) => Number.isFinite(n), { message: 'lat must be finite' }),
  long: z.coerce
    .number()
    .refine((n) => Number.isFinite(n), { message: 'long must be finite' }),
});

export const legacyCourierTrackingSchema = z.object({
  // Per-field `.catch(null)`, NOT whole-object strictness: a malformed
  // sibling (e.g. a non-point `target_destination` sentinel) must degrade to
  // "no target" rather than discarding a valid `location` with it — that
  // failure read as "map shows, courier icon doesn't".
  location: legacyTrackingPointSchema.nullable().catch(null),
  target_destination: legacyTrackingPointSchema.nullable().catch(null),
});
export type LegacyCourierTracking = z.infer<typeof legacyCourierTrackingSchema>;

export const legacyOrderApi = {
  async list(query: LegacyOrdersQuery): Promise<LegacyOrderList> {
    const raw = await legacyHttp.get<unknown>('orders/for-merchant', {
      limit: query.limit ?? LEGACY_ORDERS_PAGE_SIZE,
      skip: query.skip,
      from_date: query.fromDate,
      till_date: query.tillDate,
      // Omit rather than send an empty string: the old backend treats `search=`
      // as "match the empty id" and returns nothing.
      ...(query.search ? { search: query.search } : {}),
      ...statusParams(query.status),
    });
    return legacyOrderListSchema.parse(raw);
  },

  /**
   * One order, for the detail drawer.
   *
   * Note the response is UNENVELOPED — the order's fields are at the top level,
   * unlike `list` above which answers `{data, count, …}`. The schema parses the
   * raw body directly; there is no `.data` to unwrap.
   */
  async detail(id: number): Promise<LegacyOrderDetail> {
    const raw = await legacyHttp.get<unknown>(`orders/${id}`);
    return legacyOrderDetailSchema.parse(raw);
  },

  /**
   * Re-dispatch the courier search for an order. Returns no useful body — the
   * caller refetches the list.
   */
  reOrderCourier: (orderId: number): Promise<unknown> =>
    legacyHttp.post(`orders/${orderId}/re-order`),

  cancelOrder: ({ id, reason }: { id: number; reason: string }): Promise<unknown> =>
    legacyHttp.patch(`orders/${id}/cancel`, { reason }),

  changePaymentStatus: ({
    orderId,
    courierPaymentStatus,
  }: {
    orderId: number;
    courierPaymentStatus: number;
  }): Promise<unknown> =>
    legacyHttp.patch(`orders/${orderId}/payment/status`, {
      courier_payment_status: courierPaymentStatus,
    }),

  /** The courier's live position while an order is in flight. */
  async courierTracking({
    orderId,
    courierId,
  }: {
    orderId: number;
    courierId: number;
  }): Promise<LegacyCourierTracking> {
    const raw = await legacyHttp.get<unknown>(
      `orders/${orderId}/couriers/${courierId}/location`,
    );
    // The legacy backend mixes bare and `{data}`-enveloped responses per
    // endpoint (list is enveloped, detail is bare). Parsing the raw body
    // directly silently nulled an enveloped fix — fields just defaulted — so
    // the drawer's courier pin never rendered. Unwrap first; bare bodies pass
    // through unchanged.
    return legacyCourierTrackingSchema.parse(unwrapData(raw));
  },

  /**
   * Offer subscription for a stage-6 (courier search) order — the old drawer
   * subscribed on open and unsubscribed on close so the dispatcher pushes
   * offer events for it. Plain REST, not websocket frames.
   */
  subscribeToOrder: (orderId: number): Promise<unknown> =>
    legacyHttp.post(`orders/${orderId}/offer`),

  unsubscribeToOrder: (orderId: number): Promise<unknown> =>
    legacyHttp.delete(`orders/${orderId}/offer`),
};
